import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Search, ClipboardCheck, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { fetchAvailability } from "@/lib/stockAvailability";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const fmtDate = (s) => s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function WarehouseDetailDialog({ open, onOpenChange, warehouse, canEdit = false, onStartTransfer, onStartCount }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  // FAZ 2C — warehouse-filtered availability: rezerve/fiziksel depo-güvenilir.
  // Yolda (incoming) depo-agnostik olduğundan burada GÖSTERİLMEZ ("—").
  const [availMap, setAvailMap] = useState(null);

  useEffect(() => {
    if (!open || !warehouse) { setAvailMap(null); return; }
    fetchAvailability({ warehouseId: warehouse.id })
      .then((rows) => {
        const m = {};
        for (const r of rows) m[`${r.product_id}|${r.variant_id || ""}`] = r;
        setAvailMap(m);
      })
      .catch(() => setAvailMap(null));
  }, [open, warehouse?.id]);

  const load = async (search = "") => {
    if (!warehouse) return;
    setLoading(true);
    try {
      const params = search ? { q: search } : {};
      const { data } = await api.get(`/warehouses/${warehouse.id}/inventory`, { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open && warehouse) { setQ(""); load(""); } }, [open, warehouse?.id]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const th = "text-[10px] uppercase tracking-widest text-slate-500 font-mono";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-6xl max-h-[92vh] flex flex-col p-0" data-testid="warehouse-detail-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="tracking-tight flex items-center gap-2">
            <span className="font-mono text-blue-400 text-sm">{warehouse?.code}</span>
            <span>{warehouse?.name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6 space-y-4 overflow-y-auto flex-1">
          {/* Özet + aksiyonlar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="grid grid-cols-3 gap-2 flex-1 min-w-[300px]">
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Ürün</div>
                <div className="text-xl font-mono font-bold text-slate-100 tabular-nums" data-testid="wd-total-products">{data?.totals?.unique_products ?? "—"}</div>
              </div>
              <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam Varyant</div>
                <div className="text-xl font-mono font-bold text-slate-100 tabular-nums" data-testid="wd-total-variants">{data?.totals?.unique_variants ?? "—"}</div>
              </div>
              <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">Fiziksel Stok</div>
                <div className="text-xl font-mono font-bold text-emerald-400 tabular-nums" data-testid="wd-total-qty">{data ? fmt(data.totals.total_qty) : "—"}</div>
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-sm text-xs border-slate-700" data-testid="wd-start-transfer" onClick={() => onStartTransfer?.(warehouse)}>
                  <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Depolar Arası Transfer
                </Button>
                <Button size="sm" className="rounded-sm text-xs bg-cyan-600 hover:bg-cyan-500" data-testid="wd-start-count" onClick={() => onStartCount?.(warehouse)}>
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Stok Sayımı Başlat
                </Button>
              </div>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-500" />
            <Input data-testid="wd-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı, SKU, barkod…" className="bg-slate-950 border-slate-700 rounded-sm pl-8 max-w-md" />
          </div>
          <div className="border border-slate-800 rounded-sm overflow-hidden">
            <div className="max-h-[55vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className={th}>Ürün</TableHead>
                    <TableHead className={th}>Kod / SKU</TableHead>
                    <TableHead className={th}>Varyant</TableHead>
                    <TableHead className={th}>Barkod</TableHead>
                    <TableHead className={`${th} text-right`}>Fiziksel Stok</TableHead>
                    <TableHead className={`${th} text-right`}>Rezerve</TableHead>
                    <TableHead className={`${th} text-right`} title="Yolda miktarı depo ataması olmadan izlenir">Yolda</TableHead>
                    <TableHead className={`${th} text-right`}>Kullanılabilir</TableHead>
                    <TableHead className={th}>Birim</TableHead>
                    <TableHead className={th}>Son Hareket</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow className="border-slate-800"><TableCell colSpan={10} className="text-center py-8 text-slate-500"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></TableCell></TableRow>
                  ) : (data?.rows || []).length === 0 ? (
                    <TableRow className="border-slate-800"><TableCell colSpan={10} className="text-center py-8 text-xs text-slate-500">Sonuç yok</TableCell></TableRow>
                  ) : data.rows.map((r) => {
                    const av = availMap ? availMap[`${r.product_id}|${r.variant_id || ""}`] : null;
                    const reserved = av ? Number(av.reserved_physical || 0) + Number(av.reserved_incoming || 0) : Number(r.reserved || 0);
                    const usable = av ? Number(av.available_physical || 0) : Number(r.quantity || 0) - Number(r.reserved || 0);
                    return (
                    <TableRow key={`${r.product_id}:${r.variant_id}`} className="border-slate-800" data-testid={`wd-row-${r.product_id}-${r.variant_id || 'none'}`}>
                      <TableCell className="text-xs text-slate-200">{r.product_name}</TableCell>
                      <TableCell className="text-[11px] font-mono text-slate-400">{r.variant_sku || r.product_code || "—"}</TableCell>
                      <TableCell className="text-[11px] text-slate-300">{r.variant_label || "—"}</TableCell>
                      <TableCell className="font-mono text-[11px] text-slate-400">{r.barcode || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-emerald-400 tabular-nums font-semibold">{fmt(r.quantity)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-amber-400 tabular-nums" data-testid={`wd-row-reserved-${r.product_id}-${r.variant_id || 'none'}`}>{fmt(reserved)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-500 tabular-nums" title="Depo ataması yok" data-testid={`wd-row-yolda-${r.product_id}-${r.variant_id || 'none'}`}>—</TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-200 tabular-nums" data-testid={`wd-row-avail-${r.product_id}-${r.variant_id || 'none'}`}>{fmt(usable)}</TableCell>
                      <TableCell className="text-[11px] font-mono text-slate-500">{r.unit || "—"}</TableCell>
                      <TableCell className="text-[11px] text-slate-500">
                        {r.last_movement_at ? (
                          <>
                            <div>{fmtDate(r.last_movement_at)}</div>
                            <div className="text-[10px] font-mono">{r.last_movement_kind} {Number(r.last_movement_delta) >= 0 ? "+" : ""}{fmt(r.last_movement_delta)}</div>
                          </>
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
